import React from 'react';
import { Alert } from 'components';
import s from './styles.module.scss';

class FormErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null,
      errorType: 'general'
    };
  }

  static getDerivedStateFromError(error) {
    // Determine error type based on error message or stack
    let errorType = 'general';
    
    if (error.message.includes('validation') || error.message.includes('required')) {
      errorType = 'validation';
    } else if (error.message.includes('network') || error.message.includes('fetch')) {
      errorType = 'network';
    } else if (error.message.includes('submit') || error.message.includes('form')) {
      errorType = 'submission';
    } else if (error.message.includes('sanitize') || error.message.includes('xss')) {
      errorType = 'security';
    }

    return { 
      hasError: true, 
      error,
      errorType,
      errorId: Date.now().toString(36) + Math.random().toString(36).substr(2)
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console for debugging
    console.error('🚨 Form Error Boundary Caught Error:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      errorId: this.state.errorId,
      errorType: this.state.errorType,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    });

    // Update state with error info
    this.setState({
      errorInfo
    });

    // Show user-friendly error message based on error type
    this.showErrorMessage();
  }

  showErrorMessage = () => {
    const { errorType } = this.state;
    
    switch (errorType) {
      case 'validation':
        Alert.error('Please check your input and try again.');
        break;
      case 'network':
        Alert.error('Network error. Please check your connection and try again.');
        break;
      case 'submission':
        Alert.error('Failed to submit form. Please try again.');
        break;
      case 'security':
        Alert.error('Invalid input detected. Please check your data and try again.');
        break;
      default:
        Alert.error('Form error occurred. Please try again.');
    }
  };

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null,
      errorType: 'general'
    });
  };

  handleReset = () => {
    // Reset form data if possible
    if (this.props.onReset) {
      this.props.onReset();
    }
    
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null,
      errorType: 'general'
    });
  };

  handleGoBack = () => {
    window.history.back();
  };

  getErrorMessage = () => {
    const { errorType } = this.state;
    
    switch (errorType) {
      case 'validation':
        return {
          title: 'Form Validation Error',
          message: 'Please check your input and ensure all required fields are filled correctly.',
          icon: 'edit_off'
        };
      case 'network':
        return {
          title: 'Connection Error',
          message: 'Please check your internet connection and try submitting the form again.',
          icon: 'wifi_off'
        };
      case 'submission':
        return {
          title: 'Submission Failed',
          message: 'Unable to submit the form. Please try again or contact support if the problem persists.',
          icon: 'send'
        };
      case 'security':
        return {
          title: 'Security Error',
          message: 'Invalid input detected. Please check your data and try again.',
          icon: 'security'
        };
      default:
        return {
          title: 'Form Error',
          message: 'An unexpected error occurred while processing your form. Please try again.',
          icon: 'error_outline'
        };
    }
  };

  render() {
    if (this.state.hasError) {
      const errorMessage = this.getErrorMessage();
      
      return (
        <div className={s.errorBoundary}>
          <div className={s.errorContainer}>
            <div className={s.errorIcon}>
              <span className="material-icons">{errorMessage.icon}</span>
            </div>
            
            <h1 className={s.errorTitle}>{errorMessage.title}</h1>
            
            <p className={s.errorMessage}>
              {errorMessage.message}
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className={s.errorDetails}>
                <summary>Error Details (Development)</summary>
                <div className={s.errorStack}>
                  <strong>Error:</strong> {this.state.error.toString()}
                  <br />
                  <strong>Error ID:</strong> {this.state.errorId}
                  <br />
                  <strong>Error Type:</strong> {this.state.errorType}
                  <br />
                  <strong>Component Stack:</strong>
                  <pre>{this.state.errorInfo?.componentStack}</pre>
                </div>
              </details>
            )}

            <div className={s.errorActions}>
              <button 
                className={s.retryButton}
                onClick={this.handleRetry}
              >
                <span className="material-icons">refresh</span>
                Try Again
              </button>
              
              <button 
                className={s.homeButton}
                onClick={this.handleReset}
              >
                <span className="material-icons">clear</span>
                Reset Form
              </button>
              
              <button 
                className={s.refreshButton}
                onClick={this.handleGoBack}
              >
                <span className="material-icons">arrow_back</span>
                Go Back
              </button>
            </div>

            <div className={s.errorFooter}>
              <p>If this problem persists, please contact support.</p>
              <p>Error ID: {this.state.errorId}</p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default FormErrorBoundary; 